import {PluginConfig} from '@/plugins/core/interface';
import {CaptchaPluginInterface, CaptchaProviderName, RecaptchaOptions} from '../types';
import {ResponseInnerMessage} from '@/hosted_fields/common/types';
import Captcha from '@/captcha/Captcha';
import TurnstileCaptcha from '@/captcha/turnstile';
import GoogleRecaptcha from '@/captcha/google-recaptcha';
import HCaptcha from '@/captcha/hcaptcha';
import {debugMode} from '@/constants/environment';
import CommManagerInterface from '@/hosted_fields/master/interface';
import {RetryErrorData} from '@/utils/retry-mechanism';

export default class CaptchaProvider implements CaptchaPluginInterface {
  private captcha: Captcha;
  private loadCaptcha: Promise<unknown>;
  public options: RecaptchaOptions;
  private commMgr: CommManagerInterface;
  private errors: RetryErrorData[] = [];

  initialize(options: RecaptchaOptions): Promise<ResponseInnerMessage> {
    this.options = options;
    this.options.onError = (error) => this.logError(error);
    this.resetErrorLogs();
    if (this.loadCaptcha) {
      if (debugMode()) console.warn('returning preloaded captcha instance', this.captcha.constructor.name);
      return this.loadCaptcha;
    }
    switch (options.provider) {
      case CaptchaProviderName.TURNSTILE: {
        this.captcha = new TurnstileCaptcha(options);
        break;
      }
      case CaptchaProviderName.HCAPTCHA: {
        this.captcha = new HCaptcha(options);
        break;
      }
      case CaptchaProviderName.GOOGLE_RECAPTCHA:
      default: {
        this.captcha = new GoogleRecaptcha(options);
        break;
      }
    }

    this.loadCaptcha = this.commMgr
      .getCheckoutUtilsPlugin()
      .then((checkoutUtilsPlugin) => checkoutUtilsPlugin.getInfo())
      .then(() => this.captcha.initialize())
      .then(() => ({success: true}));

    return this.loadCaptcha;
  }

  public getErrors() {
    return this.errors;
  }

  private logError(error: RetryErrorData): void {
    this.errors.push(error);
  }

  private resetErrorLogs() {
    this.errors = [];
  }

  async generateToken(payload?: any): Promise<string | undefined> {
    const action = typeof payload === 'string' ? payload : payload && payload.action;
    if (!this.captcha) {
      if (debugMode()) {
        console.warn('Captcha provider not set. Missing siteKey and provider');
      }
      return;
    }
    this.resetErrorLogs();
    await this.loadCaptcha;
    const token = await this.captcha.generateToken(action);
    return token;
  }

  config: PluginConfig;

  async init(): Promise<void> {
    // No op
  }

  constructor(commMgr: CommManagerInterface) {
    this.commMgr = commMgr;
  }
}
